import { ApolloError, QueryResult } from '@apollo/client';
import { useCheckAccountType } from '@cmg/auth';
import { UUID } from '@cmg/common';
import defaultTo from 'lodash/defaultTo';
import isEmpty from 'lodash/isEmpty';
import { useMemo } from 'react';

import { parsePartialErrors } from '../../../graphql/parsePartialErrors';
import { useMatchingCmgEntityKeys } from '../../hooks/useMatchingCmgEntityKeys';
import {
  CheckEntitlements_OfferingEntitlementsQuery,
  CheckEntitlements_OfferingEntitlementsQueryVariables,
  useCheckEntitlements_OfferingEntitlementsQuery,
} from './graphql';

const EMPTY_ARRAY = [];

type Result = Omit<
  QueryResult<
    CheckEntitlements_OfferingEntitlementsQuery,
    CheckEntitlements_OfferingEntitlementsQueryVariables
  >,
  'data'
> & { data: readonly string[] };

export function useGetOfferingEntitlementsQuery({
  offeringId,
  withPrivateOfferings = false,
}: {
  offeringId: UUID | undefined;
  withPrivateOfferings?: boolean;
}): Result {
  const isBuySideAccount = useCheckAccountType('BUY_SIDE');
  const matchingCmgEntityKeys = useMatchingCmgEntityKeys();

  const { data, error, ...rest } = useCheckEntitlements_OfferingEntitlementsQuery({
    variables: { offeringId: offeringId!, withPrivateOfferings },
    skip: isBuySideAccount || !offeringId,
    fetchPolicy: 'cache-and-network',
    /**
     * S&T persona is not allowed to query private offerings, so we need to handle partial AUTH errors
     */
    errorPolicy: 'all',
  });
  const entitlements = useMemo(() => {
    const [offering] = defaultTo(
      isEmpty(data?.publishedOfferings?.items ?? EMPTY_ARRAY)
        ? data?.privateOfferings?.items
        : data?.publishedOfferings?.items,
      []
    );

    return (
      offering?.syndicate?.managers.find(x => matchingCmgEntityKeys.includes(x.cmgEntityKey))
        ?.entitlements ?? EMPTY_ARRAY
    );
  }, [data?.privateOfferings?.items, data?.publishedOfferings?.items, matchingCmgEntityKeys]);

  const { errors } = parsePartialErrors(error);

  if (error && !isEmpty(error)) {
    return {
      data: entitlements,
      error: new ApolloError({ graphQLErrors: errors }),
      ...rest,
    };
  }

  return {
    data: entitlements,
    ...rest,
  };
}
